import { ReactNode } from 'react';
import { Inbox, PlugZap, RefreshCw, SearchX, ShieldAlert, TriangleAlert } from 'lucide-react';
import { Button } from 'shared/components/ui/Button';
import { Card } from 'shared/components/ui/Card';
import { Skeleton, SkeletonChartCard, SkeletonMetricTile, SkeletonRegion } from 'shared/components/ui/Skeleton';

type Variant = 'empty' | 'no_results' | 'not_connected' | 'error' | 'denied';

const CONFIG: Record<Variant, { icon: typeof Inbox; title: string; tone: string }> = {
  empty: { icon: Inbox, title: 'Sem movimentação no período', tone: 'bg-chip text-ink-secondary' },
  no_results: { icon: SearchX, title: 'Nenhum resultado para os filtros aplicados', tone: 'bg-accent-soft text-primary-dark' },
  not_connected: { icon: PlugZap, title: 'Fonte de dados não conectada', tone: 'bg-chip text-ink-secondary' },
  error: { icon: TriangleAlert, title: 'Não foi possível carregar o relatório', tone: 'bg-danger-soft text-danger' },
  denied: { icon: ShieldAlert, title: 'Acesso não permitido', tone: 'bg-danger-soft text-danger' },
};

interface StateViewProps {
  variant: Variant;
  message?: string;
  actions?: ReactNode;
}

/** Estados sem dados. Cada um tem texto próprio: zero, vazio, erro e falta de acesso não se confundem. */
export function StateView({ variant, message, actions }: StateViewProps) {
  const { icon: Icon, title, tone } = CONFIG[variant];
  return (
    <Card className="flex flex-col items-center px-6 py-14 text-center" role={variant === 'error' || variant === 'denied' ? 'alert' : 'status'}>
      <span className={`flex h-12 w-12 items-center justify-center rounded-full ${tone}`}>
        <Icon className="h-6 w-6" aria-hidden="true" />
      </span>
      <h2 className="mt-4 font-heading text-lg font-semibold text-ink">{title}</h2>
      {message && <p className="mt-1 max-w-md text-sm text-ink-secondary">{message}</p>}
      {actions && <div className="mt-5 flex flex-wrap justify-center gap-2">{actions}</div>}
    </Card>
  );
}

export function RetryButton({ onClick }: { onClick: () => void }) {
  return (
    <Button onClick={onClick}>
      <RefreshCw className="h-4 w-4" aria-hidden="true" />
      Tentar novamente
    </Button>
  );
}

/** Carregamento com o formato do relatório (nunca números zerados no lugar). */
export function ReportLoading() {
  return (
    <SkeletonRegion label="Carregando relatório...">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
        {Array.from({ length: 6 }, (_, index) => (
          <SkeletonMetricTile key={index} />
        ))}
      </div>
      <div className="mt-6 grid gap-6 xl:grid-cols-2">
        <SkeletonChartCard kind="line" />
        <SkeletonChartCard kind="bars" />
      </div>
      <Skeleton className="mt-6 h-64 w-full rounded-2xl" />
    </SkeletonRegion>
  );
}
